import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, AlertCircle, X, Check } from 'lucide-react';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmation",
  message = "Êtes-vous sûr de vouloir effectuer cette action ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  type = "danger", // 'danger', 'warning', 'info'
  isLoading = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  if (!isVisible && !isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <AlertCircle className="h-10 w-10 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-10 w-10 text-amber-500" />;
      case 'info':
        return <Info className="h-10 w-10 text-blue-500" />;
      default:
        return <AlertCircle className="h-10 w-10 text-red-500" />;
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
      default:
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };
  
  const backdropClasses = `fixed inset-0 bg-black transition-opacity duration-300 ${
    isOpen ? 'opacity-50 backdrop-blur-sm' : 'opacity-0'
  }`;
  
  const modalClasses = `relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden transform transition-all duration-300 ${
    isOpen ? 'animate-modalFadeIn' : 'opacity-0 scale-95 -translate-y-3'
  }`;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? 'visible' : 'invisible'}`}>
      {/* Overlay */}
      <div 
        className={backdropClasses}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={modalClasses}>
        {/* Header with close button */}
        <div className="absolute top-0 right-0 p-2">
          <button 
            type="button" 
            className="text-gray-400 bg-transparent hover:bg-gray-100 hover:text-gray-500 rounded-lg p-1.5 inline-flex items-center"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 text-center">
          <div className="flex justify-center mb-5">
            {getIcon()}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-6">{message}</p>
          
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row-reverse gap-2 sm:gap-3 justify-center">
            <button
              type="button"
              className={`inline-flex justify-center items-center px-4 py-2 border border-transparent font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${getButtonColor()}`}
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1.5" />
                  {confirmText}
                </>
              )}
            </button>
            <button
              type="button"
              className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 